import { useSolveModeTranslation } from 'hooks/use-solve-mode';
import { useTranslateProductType } from 'hooks/use-translate-payoff';
import { useAppSelector } from 'hooks/useAppSelector';
import { useMemo } from 'react';
import { SmartQuoteParsingResult } from 'state/workspaces/smart-quote/model';
import { getPrompt } from 'state/workspaces/smart-quote/selectors';

export type SmartQuoteParsingResultWithRowNumber = SmartQuoteParsingResult & { rowNumber: number };

export const useGroupPromptResultsBySolveMode = (tabId: string, promptId: string) => {
  const results = useAppSelector(state => getPrompt(state, tabId, promptId)?.results);

  return useMemo(() => {
    if (!results) {
      return [];
    }

    const groupedResults = Object.groupBy(
      Object.values(results).filter(result => result.solveMode || result.error),
      result => {
        return result.solveMode ?? 'Unknown';
      },
    );

    return Object.entries(groupedResults).map(
      ([solveMode, solveModeResults]) =>
        [solveMode, (solveModeResults ?? []).map((result, index) => ({ ...result, rowNumber: index + 1 }))] as const,
    );
  }, [results]);
};

export const useGroupProductBySolveMode = (promptResults: Readonly<Record<string, SmartQuoteParsingResult>>) => {
  const translateProductType = useTranslateProductType();
  const translateSolveMode = useSolveModeTranslation();

  return useMemo(() => {
    const groupedResultsBySolveMode = Object.groupBy(
      Object.values(promptResults).filter(result => result.solveMode !== undefined),
      result => {
        return result.solveMode!;
      },
    );

    return Object.entries(groupedResultsBySolveMode).map(
      ([solveMode, solveModeResults]) =>
        [
          translateSolveMode(solveMode),
          new Set(
            (solveModeResults ?? []).map(result => translateProductType(result.productKey.instrument.contractName)),
          ),
        ] as const,
    );
  }, [promptResults, translateProductType, translateSolveMode]);
};
